// src/pages/ImportMigration.jsx
// Page d'import visuelle — migre les données localStorage → Supabase
// Gère : clients, fournisseurs, documents, bons de commande,
//         catalogue de base (fichier séparé) + catalogue delta (backup)

import React, { useState, useRef } from 'react'
import { supabase } from '../supabase'
import { settingsApi } from '../services/api'
import { useAuth } from '../contexts/AuthContext'

const BATCH_SIZE = 100

const getCurrentUser = async () => {
  const { data: { user } } = await supabase.auth.getUser()
  return user
}

async function batchInsertAvecProgression(table, rows, onProgress) {
  const errors = []
  let done = 0
  for (let i = 0; i < rows.length; i += BATCH_SIZE) {
    const chunk = rows.slice(i, i + BATCH_SIZE)
    const { error } = await supabase.from(table).insert(chunk)
    if (error) errors.push(`Lot ${i}–${i + chunk.length} : ${error.message}`)
    done += chunk.length
    onProgress(done, rows.length)
    await new Promise(r => setTimeout(r, 40))
  }
  return errors
}

function normaliserDocument(doc, userId) {
  return {
    numero:            doc.numero || `IMP-${Date.now()}`,
    type:              doc.type   || 'devis',
    statut:            doc.statut || 'en-attente',
    date:              doc.date   || new Date().toISOString().slice(0, 10),
    date_echeance:     doc.dateEcheance || null,
    client_snapshot:   doc.client || {},
    objet:             doc.objet  || null,
    items:             doc.items  || [],
    sous_total_ht:     parseFloat(doc.sousTotal)  || 0,
    montant_tva:       parseFloat(doc.tva)         || 0,
    total:             parseFloat(doc.total)        || 0,
    tax_mode:          doc.taxMode || 'TTC',
    remise_globale:    doc.remiseGlobale?.valeur    || 0,
    conditions:        doc.conditions || null,
    notes:             doc.notes     || null,
    methode_paiement:  doc.methodePaiement || null,
    raison_annulation: doc.raisonAnnulation || null,
    paiements:         doc.paiements || [],
    frais_supplementaires: doc.fraisSupplementaires || [],
    penalites_retard:  doc.penalitesRetard || {},
    created_by:        userId,
    updated_by:        userId,
  }
}

function normaliserClient(c, userId) {
  return {
    nom:       (c.nom || '').trim() || 'Client importé',
    email:     c.email     || null,
    telephone: c.telephone || null,
    adresse:   c.adresse   || null,
    ville:     c.ville     || null,
    pays:      c.pays      || 'Cameroun',
    nui:       c.nui       || null,
    rccm:      c.rccm      || null,
    notes:     c.notes     || null,
    actif:     true,
    created_by: userId,
    updated_by: userId,
  }
}

function normaliserFournisseur(f, userId) {
  return {
    nom:        (f.nom || '').trim() || 'Fournisseur importé',
    contact:    f.contact   || null,
    email:      f.email     || null,
    telephone:  f.telephone || null,
    adresse:    f.adresse   || null,
    ville:      f.ville     || null,
    pays:       f.pays      || 'Cameroun',
    nui:        f.nui       || null,
    rccm:       f.rccm      || null,
    notes:      f.notes     || f.compteBancaire || null,
    actif:      true,
    created_by: userId,
    updated_by: userId,
  }
}

function normaliserBC(bc, userId) {
  return {
    numero:               bc.numero || `BC-IMP-${Date.now()}`,
    statut:               bc.statut || 'brouillon',
    date:                 bc.date   || new Date().toISOString().slice(0, 10),
    date_livraison:       bc.dateLivraison || null,
    fournisseur_snapshot: bc.fournisseur || {},
    objet:                bc.objet  || null,
    items:                bc.items  || [],
    sous_total_ht:        parseFloat(bc.sousTotal) || 0,
    montant_tva:          parseFloat(bc.tva)        || 0,
    total:                parseFloat(bc.total)       || 0,
    conditions:           bc.conditions || null,
    notes:                bc.notes     || null,
    raison_annulation:    bc.raisonAnnulation || null,
    created_by:           userId,
    updated_by:           userId,
  }
}

function normaliserArticle(a, userId, isBase = false) {
  return {
    ref:                   (a.ref || '').trim(),
    code_article:          a.codeArticle          || null,
    designation:           (a.designation || '').trim() || 'Article importé',
    description:           a.description           || null,
    categorie_mere:        a.categoreMere          || null,
    code_categorie_mere:   a.codeCategorieMere     || null,
    categorie_fille:       a.categorieFille        || null,
    code_categorie_fille:  a.codeCategorieFille    || null,
    marque:                a.marque                || null,
    ref_fournisseur:       a.refFournisseur        || null,
    unite:                 a.unite                 || 'pièce',
    prix_unitaire:         parseFloat(a.prixUnitaire) || 0,
    tva:                   a.tva                   || 'TTC',
    actif:                 a.actif !== false,
    is_base:               isBase,
    created_by:            userId,
    updated_by:            userId,
  }
}

export default function ImportMigration() {
  const { isAdmin, can } = useAuth()

  if (!isAdmin() && !can('import', 'write')) {
    return (
      <div style={{
        padding: '3rem', textAlign: 'center',
        fontFamily: "'Bricolage Grotesque', sans-serif",
        color: '#EF4444',
      }}>
        🚫 Accès réservé au Super Administrateur.
      </div>
    )
  }

  const fileBackupRef  = useRef(null)
  const fileCatalogRef = useRef(null)

  const [backup,       setBackup]       = useState(null)
  const [backupName,   setBackupName]   = useState(null)
  const [catalogBase,  setCatalogBase]  = useState(null)
  const [catalogName,  setCatalogName]  = useState(null)

  const [opts, setOpts] = useState({
    clients: true, fournisseurs: true, documents: true,
    bonsCommande: true, catalogBase: true, catalogDelta: true,
    settings: true, skipDoublons: true,
  })

  const [running,  setRunning]  = useState(false)
  const [done,     setDone]     = useState(false)
  const [progress, setProgress] = useState({ etape: '', current: 0, total: 0, pct: 0 })
  const [rapport,  setRapport]  = useState(null)

  const handleBackup = async (e) => {
    const file = e.target.files?.[0]
    if (!file) return
    try {
      const data = JSON.parse(await file.text())
      setBackup(data); setBackupName(file.name)
      setDone(false); setRapport(null); setProgress({})
    } catch { alert('❌ Fichier JSON invalide.') }
  }

  const handleCatalogBase = async (e) => {
    const file = e.target.files?.[0]
    if (!file) return
    try {
      const data = JSON.parse(await file.text())
      const articles = Array.isArray(data) ? data : (data.articles || data.catalogue || [])
      if (articles.length === 0) { alert('❌ Aucun article trouvé dans ce fichier.'); return }
      setCatalogBase(articles); setCatalogName(file.name)
      setDone(false); setRapport(null); setProgress({})
    } catch { alert('❌ Fichier JSON invalide.') }
  }

  const lancerImport = async () => {
    setRunning(true); setDone(false); setRapport(null)
    const user   = await getCurrentUser()
    const userId = user?.id
    const res    = {
      clients: 0, fournisseurs: 0, documents: 0, bonsCommande: 0,
      catalogBase: 0, catalogDelta: 0, settings: false, erreurs: []
    }
    const step = (etape, current, total) =>
      setProgress({ etape, current, total, pct: total ? Math.round(current / total * 100) : 100 })

    try {
      // 1. Clients
      if (opts.clients && backup) {
        const raw = backup.solstice_clients || []
        step('👤 Clients', 0, raw.length)
        let rows = raw.map(c => normaliserClient(c, userId))
        if (opts.skipDoublons && rows.length > 0) {
          const { data: ex } = await supabase.from('clients').select('nom')
          const noms = new Set((ex || []).map(c => c.nom.toLowerCase()))
          const avant = rows.length
          rows = rows.filter(c => !noms.has(c.nom.toLowerCase()))
          if (avant - rows.length > 0) res.erreurs.push(`Clients : ${avant - rows.length} doublon(s) ignoré(s)`)
        }
        const errs = await batchInsertAvecProgression('clients', rows, (c, t) => step('👤 Clients', c, t))
        res.clients = rows.length; res.erreurs.push(...errs)
      }

      // 2. Fournisseurs
      if (opts.fournisseurs && backup) {
        const raw = backup.solstice_fournisseurs || []
        step('🏭 Fournisseurs', 0, raw.length)
        let rows = raw.map(f => normaliserFournisseur(f, userId))
        if (opts.skipDoublons && rows.length > 0) {
          const { data: ex } = await supabase.from('fournisseurs').select('nom')
          const noms = new Set((ex || []).map(f => f.nom.toLowerCase()))
          const avant = rows.length
          rows = rows.filter(f => !noms.has(f.nom.toLowerCase()))
          if (avant - rows.length > 0) res.erreurs.push(`Fournisseurs : ${avant - rows.length} doublon(s) ignoré(s)`)
        }
        const errs = await batchInsertAvecProgression('fournisseurs', rows, (c, t) => step('🏭 Fournisseurs', c, t))
        res.fournisseurs = rows.length; res.erreurs.push(...errs)
      }

      // 3. Documents
      if (opts.documents && backup) {
        const raw = backup.solstice_documents || []
        step('📄 Documents', 0, raw.length)
        let rows = raw.map(d => normaliserDocument(d, userId))
        if (opts.skipDoublons && rows.length > 0) {
          const { data: ex } = await supabase.from('documents').select('numero')
          const nums = new Set((ex || []).map(d => d.numero))
          const avant = rows.length
          rows = rows.filter(d => !nums.has(d.numero))
          if (avant - rows.length > 0) res.erreurs.push(`Documents : ${avant - rows.length} doublon(s) ignoré(s)`)
        }
        const errs = await batchInsertAvecProgression('documents', rows, (c, t) => step('📄 Documents', c, t))
        res.documents = rows.length; res.erreurs.push(...errs)
      }

      // 4. Bons de commande
      if (opts.bonsCommande && backup) {
        const raw = backup.solstice_bons_commande || []
        step('🛒 Bons de commande', 0, raw.length)
        let rows = raw.map(bc => normaliserBC(bc, userId))
        if (opts.skipDoublons && rows.length > 0) {
          const { data: ex } = await supabase.from('bons_commande').select('numero')
          const nums = new Set((ex || []).map(b => b.numero))
          const avant = rows.length
          rows = rows.filter(b => !nums.has(b.numero))
          if (avant - rows.length > 0) res.erreurs.push(`BC : ${avant - rows.length} doublon(s) ignoré(s)`)
        }
        const errs = await batchInsertAvecProgression('bons_commande', rows, (c, t) => step('🛒 Bons de commande', c, t))
        res.bonsCommande = rows.length; res.erreurs.push(...errs)
      }

      // 5. Catalogue de BASE
      if (opts.catalogBase && catalogBase && catalogBase.length > 0) {
        step('📦 Catalogue de base', 0, catalogBase.length)
        let rows = catalogBase.filter(a => a.ref && a.designation).map(a => normaliserArticle(a, userId, true))
        if (opts.skipDoublons && rows.length > 0) {
          const { data: ex } = await supabase.from('catalogue').select('ref')
          const refs = new Set((ex || []).map(a => a.ref))
          const avant = rows.length
          rows = rows.filter(a => !refs.has(a.ref))
          if (avant - rows.length > 0) res.erreurs.push(`Catalogue base : ${avant - rows.length} article(s) déjà présent(s), ignoré(s)`)
        }
        let done = 0
        for (let i = 0; i < rows.length; i += BATCH_SIZE) {
          const chunk = rows.slice(i, i + BATCH_SIZE)
          const { error } = await supabase.from('catalogue').upsert(chunk, { onConflict: 'ref', ignoreDuplicates: false })
          if (error) res.erreurs.push(`Catalogue lot ${i} : ${error.message}`)
          done += chunk.length
          step('📦 Catalogue de base', done, rows.length)
          await new Promise(r => setTimeout(r, 40))
        }
        res.catalogBase = rows.length
      }

      // 6. Catalogue DELTA (modifications personnelles)
      if (opts.catalogDelta && backup) {
        const delta = backup.solstice_catalogue_delta || []
        if (delta.length > 0) {
          step('✏️ Catalogue personnalisé', 0, delta.length)
          const ajouts   = delta.filter(d => d.action === 'add'    && d.article)
          const editions = delta.filter(d => d.action === 'edit'   && d.article)
          const supprs   = delta.filter(d => d.action === 'delete' && d.ref)
          if (ajouts.length > 0) {
            const rows = ajouts.map(d => normaliserArticle(d.article, userId, false))
            const { error } = await supabase.from('catalogue').upsert(rows, { onConflict: 'ref', ignoreDuplicates: true })
            if (error) res.erreurs.push(`Delta ajouts : ${error.message}`)
            res.catalogDelta += ajouts.length
          }
          for (const d of editions) {
            const row = normaliserArticle(d.article, userId, false)
            const { error } = await supabase.from('catalogue').upsert(row, { onConflict: 'ref' })
            if (error) res.erreurs.push(`Delta edit ${d.article.ref} : ${error.message}`)
            res.catalogDelta++
          }
          for (const d of supprs) {
            const { error } = await supabase.from('catalogue').update({ actif: false }).eq('ref', d.ref)
            if (error) res.erreurs.push(`Delta delete ${d.ref} : ${error.message}`)
          }
          step('✏️ Catalogue personnalisé', delta.length, delta.length)
        } else {
          res.erreurs.push('Catalogue delta : aucune modification personnelle trouvée dans le backup')
        }
      }

      // 7. Paramètres
      if (opts.settings && backup?.solstice_settings) {
        step('⚙️ Paramètres', 0, 1)
        try { await settingsApi.update(backup.solstice_settings); res.settings = true }
        catch (err) { res.erreurs.push('Paramètres : ' + err.message) }
        step('⚙️ Paramètres', 1, 1)
      }

    } catch (err) {
      res.erreurs.push('Erreur inattendue : ' + err.message)
    }

    setRapport(res); setRunning(false); setDone(true); setProgress({})
  }

  const cpt = {
    clients:      (backup?.solstice_clients      || []).length,
    fournisseurs: (backup?.solstice_fournisseurs || []).length,
    documents:    (backup?.solstice_documents    || []).length,
    bonsCommande: (backup?.solstice_bons_commande|| []).length,
    catalogDelta: (backup?.solstice_catalogue_delta || []).length,
    catalogBase:  catalogBase?.length || 0,
    settings:     !!backup?.solstice_settings,
  }


  const card = {
    background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)',
    borderRadius: 16, padding: '1.5rem', marginBottom: '1.25rem',
  }
  const dropZone = (loaded) => ({
    border: `2px dashed ${loaded ? '#10B981' : 'rgba(255,255,255,0.15)'}`,
    borderRadius: 12, padding: '1.5rem', textAlign: 'center', cursor: 'pointer',
    background: loaded ? 'rgba(16,185,129,0.06)' : 'rgba(255,255,255,0.02)',
    transition: 'all 0.2s',
  })

  return (
    <div style={{ padding: '2rem', fontFamily: "'Bricolage Grotesque', sans-serif",
      color: 'white', maxWidth: 820, margin: '0 auto' }}>

      <div style={{ marginBottom: '2rem' }}>
        <h2 style={{ margin: 0, fontSize: '1.75rem', fontWeight: 800 }}>📥 Import des données</h2>
        <p style={{ color: '#64748B', marginTop: '0.4rem', fontSize: '0.9rem', lineHeight: 1.6 }}>
          Migrez vos données depuis l'ancien SOLSTICE v67 vers Supabase.
        </p>
      </div>

      {/* ── ÉTAPE 1 : Fichiers ─────────────────────────────────────────────── */}
      <div style={card}>
        <h3 style={{ margin: '0 0 1.25rem', fontSize: '1rem', fontWeight: 700, color: '#CBD5E1' }}>
          Étape 1 — Charger vos fichiers
        </h3>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
          {/* Backup */}
          <div>
            <div style={{ color: '#94A3B8', fontSize: '0.78rem', marginBottom: '0.5rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              📋 Fichier backup <span style={{ color: '#FF6B35' }}>*obligatoire</span>
            </div>
            <div style={dropZone(!!backup)} onClick={() => fileBackupRef.current?.click()}>
              <input ref={fileBackupRef} type="file" accept=".json" onChange={handleBackup} style={{ display: 'none' }} />
              {backup ? (
                <><div style={{ fontSize: '1.5rem' }}>✅</div>
                  <div style={{ color: '#10B981', fontWeight: 700, fontSize: '0.85rem', marginTop: '0.25rem' }}>{backupName}</div>
                  <div style={{ color: '#64748B', fontSize: '0.72rem', marginTop: '0.15rem' }}>
                    {backup.exportedAt ? new Date(backup.exportedAt).toLocaleDateString('fr-FR') : ''} · {backup.version || 'v67'}
                  </div></>
              ) : (
                <><div style={{ fontSize: '1.75rem' }}>📂</div>
                  <div style={{ color: '#94A3B8', fontSize: '0.85rem', marginTop: '0.25rem', fontWeight: 600 }}>solstice-backup-XXXX.json</div>
                  <div style={{ color: '#475569', fontSize: '0.75rem', marginTop: '0.2rem' }}>Clients, factures, fournisseurs…</div></>
              )}
            </div>
          </div>

          {/* Catalogue base */}
          <div>
            <div style={{ color: '#94A3B8', fontSize: '0.78rem', marginBottom: '0.5rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              📦 Catalogue de base <span style={{ color: '#64748B' }}>(optionnel)</span>
            </div>
            <div style={dropZone(!!catalogBase)} onClick={() => fileCatalogRef.current?.click()}>
              <input ref={fileCatalogRef} type="file" accept=".json" onChange={handleCatalogBase} style={{ display: 'none' }} />
              {catalogBase ? (
                <><div style={{ fontSize: '1.5rem' }}>✅</div>
                  <div style={{ color: '#10B981', fontWeight: 700, fontSize: '0.85rem', marginTop: '0.25rem' }}>{catalogName}</div>
                  <div style={{ color: '#64748B', fontSize: '0.72rem', marginTop: '0.15rem' }}>
                    {catalogBase.length.toLocaleString('fr-FR')} articles détectés
                  </div></>
              ) : (
                <><div style={{ fontSize: '1.75rem' }}>📦</div>
                  <div style={{ color: '#94A3B8', fontSize: '0.85rem', marginTop: '0.25rem', fontWeight: 600 }}>solstice-catalogue-base-XXXX.json</div>
                  <div style={{ color: '#475569', fontSize: '0.75rem', marginTop: '0.2rem' }}>~10 000 articles standards</div></>
              )}
            </div>
          </div>
        </div>

        {/* Instructions */}
        <details style={{ marginTop: '1.25rem' }}>
          <summary style={{ color: '#64748B', fontSize: '0.85rem', cursor: 'pointer', userSelect: 'none', listStyle: 'none' }}>
            💡 Comment obtenir ces fichiers ? (cliquez pour voir les codes)
          </summary>
          <div style={{ background: 'rgba(0,0,0,0.3)', borderRadius: 12, padding: '1.25rem',
            marginTop: '0.75rem', fontSize: '0.82rem', lineHeight: 1.7, color: '#94A3B8' }}>
            <p style={{ margin: '0 0 0.5rem', color: 'white', fontWeight: 600 }}>
              Dans l'ancien SOLSTICE : ouvrez le HTML dans Chrome → F12 → Console
            </p>
            <p style={{ color: '#FF6B35', fontWeight: 700, margin: '0.75rem 0 0.25rem' }}>
              ▶ Code 1 — Backup (données métier + modifications catalogue) :
            </p>
            <pre style={{ background: '#0F172A', borderRadius: 8, padding: '0.9rem', overflow: 'auto',
              fontSize: '0.74rem', color: '#00D4AA', margin: '0 0 1rem', whiteSpace: 'pre-wrap' }}>
{`(function(){const keys=['solstice_documents','solstice_clients',
  'solstice_fournisseurs','solstice_bons_commande',
  'solstice_settings','solstice_catalogue_delta'];
const out={exportedAt:new Date().toISOString(),version:'v67'};
keys.forEach(k=>{try{const r=localStorage.getItem(k);
  if(r)out[k]=JSON.parse(r);}catch(e){}});
const a=document.createElement('a');
a.href=URL.createObjectURL(new Blob([JSON.stringify(out,null,2)],
  {type:'application/json'}));
a.download='solstice-backup-'+new Date().toISOString().slice(0,10)+'.json';
a.click();console.log('✅ Backup exporté');})();`}
            </pre>
            <p style={{ color: '#FF6B35', fontWeight: 700, margin: '0 0 0.25rem' }}>
              ▶ Code 2 — Catalogue de base (~10 000 articles) :
            </p>
            <pre style={{ background: '#0F172A', borderRadius: 8, padding: '0.9rem', overflow: 'auto',
              fontSize: '0.74rem', color: '#00D4AA', margin: 0, whiteSpace: 'pre-wrap' }}>
{`(function(){const articles=window.__CATALOGUE__||[];
if(articles.length===0){console.log('❌ Attendez le chargement complet.');return;}
const a=document.createElement('a');
a.href=URL.createObjectURL(new Blob([JSON.stringify(articles,null,2)],
  {type:'application/json'}));
a.download='solstice-catalogue-base-'+new Date().toISOString().slice(0,10)+'.json';
a.click();console.log('✅ Catalogue: '+articles.length+' articles');})();`}
            </pre>
            <p style={{ margin: '0.75rem 0 0', color: '#64748B', fontSize: '0.78rem' }}>
              ⚠️ Attendez que la page soit entièrement chargée avant d'exécuter le Code 2.
              Le fichier catalogue peut faire plusieurs Mo.
            </p>
          </div>
        </details>
      </div>

      {/* ── ÉTAPE 2 : Sélection ───────────────────────────────────────────── */}
      {(backup || catalogBase) && (
        <div style={card}>
          <h3 style={{ margin: '0 0 1.25rem', fontSize: '1rem', fontWeight: 700, color: '#CBD5E1' }}>
            Étape 2 — Sélectionner ce à importer
          </h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px,1fr))',
            gap: '0.65rem', marginBottom: '1.25rem' }}>
            {[
              { opt: 'clients',      icon: '👤', label: 'Clients',         count: cpt.clients,      src: 'backup' },
              { opt: 'fournisseurs', icon: '🏭', label: 'Fournisseurs',    count: cpt.fournisseurs, src: 'backup' },
              { opt: 'documents',    icon: '📄', label: 'Documents',       count: cpt.documents,    src: 'backup' },
              { opt: 'bonsCommande', icon: '🛒', label: 'Bons commande',   count: cpt.bonsCommande, src: 'backup' },
              { opt: 'catalogBase',  icon: '📦', label: 'Catalogue base',  count: cpt.catalogBase,  src: 'catalog' },
              { opt: 'catalogDelta', icon: '✏️', label: 'Mes modifs cat.', count: cpt.catalogDelta, src: 'backup' },
              { opt: 'settings',     icon: '⚙️', label: 'Paramètres',     count: cpt.settings?1:0, src: 'backup' },
            ].map(({ opt, icon, label, count, src }) => {
              const dispo = src === 'backup' ? !!backup : !!catalogBase
              const actif = opts[opt] && dispo
              return (
                <label key={opt} style={{
                  display: 'flex', alignItems: 'center', gap: '0.6rem',
                  background: !dispo ? 'rgba(255,255,255,0.01)' : actif ? 'rgba(255,107,53,0.1)' : 'rgba(255,255,255,0.03)',
                  border: `1px solid ${!dispo ? 'rgba(255,255,255,0.04)' : actif ? 'rgba(255,107,53,0.4)' : 'rgba(255,255,255,0.06)'}`,
                  borderRadius: 12, padding: '0.85rem 0.9rem',
                  cursor: dispo ? 'pointer' : 'not-allowed', opacity: dispo ? 1 : 0.4, transition: 'all 0.2s',
                }}>
                  <input type="checkbox" checked={opts[opt] && dispo} disabled={!dispo}
                    onChange={e => setOpts(p => ({ ...p, [opt]: e.target.checked }))}
                    style={{ accentColor: '#FF6B35', width: 15, height: 15, flexShrink: 0 }} />
                  <div>
                    <div style={{ fontSize: '0.72rem', color: '#94A3B8' }}>{icon} {label}</div>
                    <div style={{ fontSize: '1.3rem', fontWeight: 800, color: count > 0 ? 'white' : '#475569' }}>
                      {dispo ? (count > 0 ? count.toLocaleString('fr-FR') : '0') : '—'}
                    </div>
                  </div>
                </label>
              )
            })}
          </div>
          <label style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem',
            color: '#94A3B8', fontSize: '0.875rem', cursor: 'pointer' }}>
            <input type="checkbox" checked={opts.skipDoublons}
              onChange={e => setOpts(p => ({ ...p, skipDoublons: e.target.checked }))}
              style={{ accentColor: '#FF6B35', width: 16, height: 16, marginTop: 2, flexShrink: 0 }} />
            <span>
              <strong style={{ color: 'white' }}>Ignorer les doublons</strong>
              {' '}— saute les enregistrements déjà présents dans la base.{' '}
              <span style={{ color: '#10B981' }}>(recommandé)</span>
            </span>
          </label>
        </div>
      )}

      {/* ── ÉTAPE 3 : Lancer ──────────────────────────────────────────────── */}
      {(backup || catalogBase) && !running && !done && (
        <div style={card}>
          <h3 style={{ margin: '0 0 0.75rem', fontSize: '1rem', fontWeight: 700, color: '#CBD5E1' }}>
            Étape 3 — Lancer l'import
          </h3>
          <p style={{ color: '#64748B', fontSize: '0.875rem', margin: '0 0 1.25rem', lineHeight: 1.6 }}>
            Les données seront insérées dans votre base Supabase.
            <strong style={{ color: '#F59E0B' }}> Ne fermez pas la fenêtre</strong> pendant l'opération.
            {cpt.catalogBase > 0 && (
              <span style={{ display: 'block', marginTop: '0.4rem', color: '#94A3B8' }}>
                ⏱️ Le catalogue de base ({cpt.catalogBase.toLocaleString('fr-FR')} articles) peut prendre 2 à 5 minutes.
              </span>
            )}
          </p>
          <button onClick={lancerImport} style={{
            background: 'linear-gradient(135deg,#FF6B35,#E55A2B)', border: 'none',
            borderRadius: 14, padding: '1rem 2.5rem', color: 'white',
            fontSize: '1rem', fontWeight: 700, cursor: 'pointer',
            boxShadow: '0 8px 24px rgba(255,107,53,0.3)',
          }}>
            🚀 Lancer l'import maintenant
          </button>
        </div>
      )}

      {/* ── Progression ───────────────────────────────────────────────────── */}
      {running && (
        <div style={{ ...card, border: '1px solid rgba(255,107,53,0.3)' }}>
          <h3 style={{ margin: '0 0 1.25rem', fontSize: '1rem', fontWeight: 700, color: '#FF6B35' }}>
            ⏳ Import en cours…
          </h3>
          <div style={{ display: 'flex', justifyContent: 'space-between',
            color: '#CBD5E1', fontSize: '0.875rem', marginBottom: '0.5rem' }}>
            <span>{progress.etape || 'Initialisation…'}</span>
            <span style={{ color: '#64748B' }}>
              {(progress.current || 0).toLocaleString('fr-FR')} / {(progress.total || 0).toLocaleString('fr-FR')}
            </span>
          </div>
          <div style={{ height: 12, background: 'rgba(255,255,255,0.08)', borderRadius: 99, overflow: 'hidden' }}>
            <div style={{
              height: '100%', borderRadius: 99,
              background: 'linear-gradient(90deg,#FF6B35,#00D4AA)',
              width: `${progress.pct || 0}%`, transition: 'width 0.4s ease',
            }} />
          </div>
          <p style={{ color: '#475569', fontSize: '0.78rem', margin: '0.75rem 0 0' }}>
            Ne fermez pas cette fenêtre pendant l'import.
          </p>
        </div>
      )}

      {/* ── Rapport final ─────────────────────────────────────────────────── */}
      {done && rapport && (() => {
        const vraisErreurs = rapport.erreurs.filter(e => !e.includes('ignoré') && !e.includes('aucune modification'))
        const warnings     = rapport.erreurs.filter(e =>  e.includes('ignoré') ||  e.includes('aucune modification'))
        const succes = vraisErreurs.length === 0
        return (
          <div style={{ ...card, border: `1px solid ${succes ? 'rgba(16,185,129,0.4)' : 'rgba(245,158,11,0.4)'}` }}>
            <h3 style={{ margin: '0 0 1.25rem', fontSize: '1.1rem', fontWeight: 800, color: succes ? '#10B981' : '#F59E0B' }}>
              {succes ? '✅ Import terminé avec succès !' : '⚠️ Import terminé avec avertissements'}
            </h3>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(120px,1fr))',
              gap: '0.6rem', marginBottom: '1.25rem' }}>
              {[
                { icon: '👤', label: 'Clients',       count: rapport.clients },
                { icon: '🏭', label: 'Fournisseurs',  count: rapport.fournisseurs },
                { icon: '📄', label: 'Documents',     count: rapport.documents },
                { icon: '🛒', label: 'Bons commande', count: rapport.bonsCommande },
                { icon: '📦', label: 'Cat. base',     count: rapport.catalogBase },
                { icon: '✏️', label: 'Cat. modifs',  count: rapport.catalogDelta },
              ].map(({ icon, label, count }) => (
                <div key={label} style={{ background: 'rgba(16,185,129,0.08)',
                  border: '1px solid rgba(16,185,129,0.2)', borderRadius: 10,
                  padding: '0.75rem', textAlign: 'center' }}>
                  <div style={{ color: '#6EE7B7', fontSize: '0.7rem' }}>{icon} {label}</div>
                  <div style={{ color: 'white', fontSize: '1.4rem', fontWeight: 800 }}>
                    {count.toLocaleString('fr-FR')}
                  </div>
                  <div style={{ color: '#10B981', fontSize: '0.65rem' }}>importé(s)</div>
                </div>
              ))}
            </div>
            {rapport.settings && <div style={{ color: '#6EE7B7', fontSize: '0.85rem', marginBottom: '0.75rem' }}>⚙️ Paramètres société importés</div>}
            {vraisErreurs.length > 0 && (
              <div style={{ background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.25)',
                borderRadius: 10, padding: '0.9rem', marginBottom: '0.75rem' }}>
                <div style={{ color: '#FCA5A5', fontWeight: 700, fontSize: '0.85rem', marginBottom: '0.4rem' }}>❌ Erreurs :</div>
                {vraisErreurs.map((e, i) => <div key={i} style={{ color: '#EF4444', fontSize: '0.78rem', padding: '0.15rem 0' }}>• {e}</div>)}
              </div>
            )}
            {warnings.length > 0 && (
              <div style={{ background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.2)',
                borderRadius: 10, padding: '0.9rem', marginBottom: '0.75rem' }}>
                <div style={{ color: '#FCD34D', fontWeight: 700, fontSize: '0.85rem', marginBottom: '0.4rem' }}>ℹ️ Informations :</div>
                {warnings.map((e, i) => <div key={i} style={{ color: '#D97706', fontSize: '0.78rem', padding: '0.15rem 0' }}>• {e}</div>)}
              </div>
            )}
            <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', marginTop: '1rem' }}>
              <button onClick={() => {
                setBackup(null); setBackupName(null); setCatalogBase(null); setCatalogName(null)
                setDone(false); setRapport(null)
                if (fileBackupRef.current)  fileBackupRef.current.value  = ''
                if (fileCatalogRef.current) fileCatalogRef.current.value = ''
              }} style={{ background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.12)',
                borderRadius: 10, padding: '0.65rem 1.25rem', color: '#CBD5E1',
                cursor: 'pointer', fontWeight: 600, fontSize: '0.875rem' }}>
                🔄 Nouvel import
              </button>
              <button onClick={() => window.location.reload()} style={{
                background: 'linear-gradient(135deg,#FF6B35,#E55A2B)', border: 'none',
                borderRadius: 10, padding: '0.65rem 1.25rem', color: 'white',
                cursor: 'pointer', fontWeight: 700, fontSize: '0.875rem',
                boxShadow: '0 4px 16px rgba(255,107,53,0.3)' }}>
                ↩️ Retourner à SOLSTICE
              </button>
            </div>
          </div>
        )
      })()}
    </div>
  )
}
